import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getWorkShiftDetails,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";
import { toast } from "react-toastify";
import { FaArrowRight, FaEdit, FaTrash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";

const ShiftDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const { workShiftDetails, isLoading, error, success } = useSelector(
    state => state.auth,
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const lastErrorRef = useRef({ message: null, time: 0 });

  const daysOfWeek = [
    { key: "saturday", label: t("shifts.add.dayLabels.saturday") },
    { key: "sunday", label: t("shifts.add.dayLabels.sunday") },
    { key: "monday", label: t("shifts.add.dayLabels.monday") },
    { key: "tuesday", label: t("shifts.add.dayLabels.tuesday") },
    { key: "wednesday", label: t("shifts.add.dayLabels.wednesday") },
    { key: "thursday", label: t("shifts.add.dayLabels.thursday") },
    { key: "friday", label: t("shifts.add.dayLabels.friday") },
  ];

  useEffect(() => {
    if (id) {
      dispatch(getWorkShiftDetails(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const handleEdit = () => {
    navigate(`/shifts/edit/${id}`);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
  };

  if (isLoading) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #333",
              borderTop: "4px solid #AC2000",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <p>{t("shifts.details.loading")}</p>
        </div>
      </div>
    );
  }

  if (!workShiftDetails) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <p>{t("shifts.details.noData")}</p>
          <button
            onClick={() => navigate("/shifts")}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
              marginTop: "20px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            {t("shifts.details.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
      }}
    >
      <div
        style={{
          marginBottom: "30px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            marginBottom: "20px",
            color: "white",
            margin: 0,
            textAlign,
          }}
        >
          {t("shifts.details.title")}
        </h1>
        <button
          onClick={() => navigate("/shifts")}
          style={{
            backgroundColor: "#666",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <FaArrowRight />
          {t("shifts.details.back")}
        </button>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "30px",
          border: "1px solid #333",
          marginBottom: "30px",
        }}
      >
        <div style={{ marginBottom: "30px" }}>
          <h3
            style={{
              color: "white",
              marginBottom: "20px",
              fontSize: "18px",
              fontWeight: "bold",
              textAlign,
            }}
          >
            {t("shifts.details.sections.shiftInfo")}
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            <div style={{ textAlign }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#666",
                  fontSize: "14px",
                  textAlign,
                }}
              >
                {t("shifts.details.fields.nameAr")}
              </label>
              <p
                style={{
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                  margin: 0,
                  textAlign,
                }}
              >
                {workShiftDetails.name_ar || workShiftDetails.name}
              </p>
            </div>

            <div style={{ textAlign }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#666",
                  fontSize: "14px",
                  textAlign,
                }}
              >
                {t("shifts.details.fields.nameEn")}
              </label>
              <p
                style={{
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                  margin: 0,
                  textAlign,
                }}
              >
                {workShiftDetails.name_en || workShiftDetails.name}
              </p>
            </div>

            <div style={{ textAlign }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#666",
                  fontSize: "14px",
                  textAlign,
                }}
              >
                {t("shifts.details.fields.workDays")}
              </label>
              <p
                style={{
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                  margin: 0,
                  textAlign,
                }}
              >
                {workShiftDetails.work_days_count
                  ? `${workShiftDetails.work_days_count} ${t("shifts.details.days")}`
                  : "--"}
              </p>
            </div>

            <div style={{ textAlign }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#666",
                  fontSize: "14px",
                  textAlign,
                }}
              >
                {t("shifts.details.fields.status")}
              </label>
              <span
                style={{
                  backgroundColor: workShiftDetails.is_active
                    ? "#AC2000"
                    : "#dc3545",
                  color: "white",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  display: "inline-block",
                  textAlign,
                }}
              >
                {workShiftDetails.is_active
                  ? t("shifts.status.active")
                  : t("shifts.status.inactive")}
              </span>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "30px" }}>
          <h3
            style={{
              color: "white",
              marginBottom: "20px",
              fontSize: "18px",
              fontWeight: "bold",
              textAlign,
            }}
          >
            {t("shifts.details.sections.daysDetails")}
          </h3>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            {workShiftDetails.details &&
              workShiftDetails.details.map((day, index) => (
                <div
                  key={day.day_of_week}
                  style={{
                    backgroundColor: "#1a1f2e",
                    padding: "20px",
                    borderRadius: "8px",
                    border: "1px solid #333",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "15px",
                    }}
                  >
                    <h4
                      style={{
                        color: "white",
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      {daysOfWeek.find(d => d.key === day.day_of_week)?.label}
                    </h4>

                    <span
                      style={{
                        backgroundColor: day.is_off_day ? "#dc3545" : "#AC2000",
                        color: "white",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {day.is_off_day
                        ? t("shifts.details.dayStatus.offDay")
                        : t("shifts.details.dayStatus.workDay")}
                    </span>
                  </div>

                  {!day.is_off_day && (
                    <div
                      style={{
                        display: "flex",
                        gap: "15px",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "5px",
                            color: "#666",
                            fontSize: "14px",
                            textAlign,
                          }}
                        >
                          {t("shifts.details.timeFields.startTime")}
                        </label>
                        <p
                          style={{
                            color: "white",
                            fontSize: "16px",
                            fontWeight: "bold",
                            margin: 0,
                            textAlign,
                          }}
                        >
                          {day.start_time || "--"}
                        </p>
                      </div>

                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "5px",
                            color: "#666",
                            fontSize: "14px",
                            textAlign,
                          }}
                        >
                          {t("shifts.details.timeFields.endTime")}
                        </label>
                        <p
                          style={{
                            color: "white",
                            fontSize: "16px",
                            fontWeight: "bold",
                            margin: 0,
                            textAlign,
                          }}
                        >
                          {day.end_time || "--"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "15px",
            justifyContent: "center",
            marginTop: "30px",
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <button
            onClick={handleEdit}
            style={{
              backgroundColor: "#B3B3B3",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaEdit />
            {t("shifts.details.buttons.edit")}
          </button>

          <button
            onClick={handleDelete}
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaTrash />
            {t("shifts.details.buttons.delete")}
          </button>
        </div>
      </div>

      {showDeleteModal &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 999999,
            }}
          >
            <div
              style={{
                backgroundColor: "#1b3350",
                borderRadius: "12px",
                padding: "30px",
                maxWidth: "400px",
                width: "90%",
                border: "1px solid #333",
                direction: dir,
              }}
            >
              <h3
                style={{
                  color: "white",
                  marginBottom: "20px",
                  fontSize: "18px",
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                {t("shifts.details.deleteModal.title")}
              </h3>
              <p
                style={{
                  color: "white",
                  marginBottom: "10px",
                  textAlign: "center",
                  fontSize: "16px",
                }}
              >
                {t("shifts.details.deleteModal.message", {
                  name: workShiftDetails.name_ar || workShiftDetails.name,
                })}
              </p>
              <p
                style={{
                  color: "white",
                  marginBottom: "30px",
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                {t("shifts.details.deleteModal.warning")}
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  justifyContent: "center",
                  flexDirection: isRTL ? "row" : "row-reverse",
                }}
              >
                <button
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    backgroundColor: "#666",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("shifts.details.deleteModal.cancel")}
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("shifts.details.deleteModal.confirm")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ShiftDetails;
